/**
 * ChatPanel - Chat interface for PRD Review collaboration.
 *
 * Displays conversation history and provides input for user messages.
 * Shows typing indicator when agent is processing.
 * Shows pending message indicator when user message is being sent.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Avatar, Button, Card, CardBody, Spinner } from '@heroui/react';
import { Bot, Send, User } from 'lucide-react';

import type { ReviewChatMessage } from '~/types';

interface ChatMessageProps {
  message: ReviewChatMessage;
  showDraftButton?: boolean;
  isDraftDisabled?: boolean;
  onDraftAnswer?: () => void;
}

interface ChatPanelProps {
  chatHistory: ReviewChatMessage[];
  isAgentTyping: boolean;
  isDisabled: boolean;
  /** Content of message currently being sent (for optimistic UI) */
  pendingMessageContent: string | null;
  onSendMessage: (content: string) => void;
}

// -----------------------------------------------------------------------------
// ChatMessage sub-component
// -----------------------------------------------------------------------------

function ChatMessage({
  isDraftDisabled,
  message,
  onDraftAnswer,
  showDraftButton,
}: ChatMessageProps): JSX.Element {
  const isAgent = message.role === 'agent';

  return (
    <div
      className={`flex gap-3 mb-4 ${isAgent ? 'justify-start' : 'justify-end'}`}
      role="article"
      aria-label={`${isAgent ? 'AI assistant' : 'Your'} message`}
    >
      {isAgent && (
        <Avatar
          icon={<Bot className="w-5 h-5" />}
          classNames={{
            base: 'bg-primary/10 shrink-0',
            icon: 'text-primary',
          }}
          size="sm"
        />
      )}

      <Card
        classNames={{
          base: `max-w-[85%] ${isAgent ? 'bg-surface-elevated' : 'bg-primary/10'}`,
        }}
      >
        <CardBody className="p-3">
          {isAgent ? (
            <div className="text-sm">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-xl font-bold mt-4 mb-2 text-foreground">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-lg font-semibold mt-3 mb-2 text-foreground">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-base font-medium mt-2 mb-1 text-foreground">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="mb-2 text-foreground leading-relaxed">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>
                  ),
                  li: ({ children }) => <li className="text-foreground">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  em: ({ children }) => <em>{children}</em>,
                  code: ({ children }) => (
                    <code className="bg-default-100 px-1 py-0.5 rounded text-xs">{children}</code>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          )}
          {showDraftButton && onDraftAnswer && (
            <div className="mt-3 pt-3 border-t border-border/30 flex justify-end">
              <Button
                size="sm"
                color="primary"
                isDisabled={isDraftDisabled}
                onPress={onDraftAnswer}
              >
                Draft Answer
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {!isAgent && (
        <Avatar
          icon={<User className="w-5 h-5" />}
          classNames={{
            base: 'bg-primary shrink-0',
            icon: 'text-primary-foreground',
          }}
          size="sm"
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PendingMessage sub-component - Shows message being sent
// -----------------------------------------------------------------------------

function PendingMessage({ content }: { content: string }): JSX.Element {
  return (
    <div className="flex gap-3 mb-4 justify-end" role="article" aria-label="Sending message">
      <Card classNames={{ base: 'max-w-[85%] bg-primary/10 opacity-70' }}>
        <CardBody className="p-3">
          <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
          <div className="flex items-center gap-1 mt-2 text-xs text-foreground/50">
            <Spinner size="sm" color="current" />
            <span>Sending...</span>
          </div>
        </CardBody>
      </Card>
      <Avatar
        icon={<User className="w-5 h-5" />}
        classNames={{
          base: 'bg-primary shrink-0 opacity-70',
          icon: 'text-primary-foreground',
        }}
        size="sm"
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// ChatPanel main component
// -----------------------------------------------------------------------------

export function ChatPanel({
  chatHistory,
  isAgentTyping,
  isDisabled,
  onSendMessage,
  pendingMessageContent,
}: ChatPanelProps): JSX.Element {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevIsAgentTyping = useRef<boolean>(isAgentTyping);

  // Guard against rapid clicks - track if a message send is in flight
  // This prevents the race condition where React hasn't re-rendered yet
  // but the user clicks again before isDisabled updates
  const isMessageInFlightRef = useRef<boolean>(false);

  // Reset in-flight guard when pendingMessageContent changes
  // (either message was accepted, or an error cleared it)
  useEffect(() => {
    if (!pendingMessageContent) {
      isMessageInFlightRef.current = false;
    }
  }, [pendingMessageContent]);

  // Auto-focus input on mount so user can start typing immediately
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []); // Empty deps = run once on mount

  // Re-focus input when agent STOPS typing (transition from true -> false)
  // This prevents redundant focus calls on mount and only fires on actual state changes
  useEffect(() => {
    if (prevIsAgentTyping.current && !isAgentTyping && textareaRef.current) {
      textareaRef.current.focus();
    }
    prevIsAgentTyping.current = isAgentTyping;
  }, [isAgentTyping]);

  // Auto-scroll to bottom when new messages arrive or pending message shows
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isAgentTyping, pendingMessageContent]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [message]);

  const handleSubmit = useCallback((): void => {
    const trimmed = message.trim();
    // Check both React state (isDisabled) and ref guard (isMessageInFlightRef)
    // The ref guard catches rapid clicks before React re-renders
    if (trimmed && !isDisabled && !isMessageInFlightRef.current) {
      isMessageInFlightRef.current = true;
      onSendMessage(trimmed);
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        // Re-focus input so user can immediately type next message
        textareaRef.current.focus();
      }
    }
  }, [message, isDisabled, onSendMessage]);

  const handleDraftAnswer = useCallback((): void => {
    // Check both React state (isDisabled) and ref guard (isMessageInFlightRef)
    // The ref guard catches rapid clicks before React re-renders
    if (!isDisabled && !isMessageInFlightRef.current) {
      isMessageInFlightRef.current = true;
      onSendMessage('Draft an answer for me to review.');
    }
  }, [isDisabled, onSendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Submit on Enter (without Shift) or Cmd/Ctrl+Enter
    if (e.key === 'Enter' && (!e.shiftKey || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSend = message.trim().length > 0 && !isDisabled;

  // Check if we should show the Draft Answer button on the last agent message
  const lastAgentMessageIndex = chatHistory.reduce(
    (lastIdx, msg, idx) => (msg.role === 'agent' ? idx : lastIdx),
    -1,
  );
  const showDraftButtonOnMessage = (index: number): boolean => {
    return index === lastAgentMessageIndex && !isAgentTyping && !pendingMessageContent;
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="shrink-0 h-[72px] px-4 py-3 border-b border-border bg-surface-elevated flex flex-col justify-center">
        <h3 className="text-sm font-medium text-foreground">Review Chat</h3>
        <p className="text-xs text-muted mt-0.5">Discuss changes with the AI assistant</p>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4">
        {chatHistory.map((msg, index) => (
          <ChatMessage
            key={msg.message_id}
            isDraftDisabled={isDisabled}
            message={msg}
            onDraftAnswer={handleDraftAnswer}
            showDraftButton={showDraftButtonOnMessage(index)}
          />
        ))}

        {/* Pending message indicator (optimistic UI) - only show if not already in chatHistory */}
        {pendingMessageContent &&
          !chatHistory.some(
            msg => msg.role === 'user' && msg.content === pendingMessageContent,
          ) && <PendingMessage content={pendingMessageContent} />}

        {/* Typing indicator - show when chat is empty (first message) OR when agent is typing */}
        {(chatHistory.length === 0 || isAgentTyping) && (
          <div className="flex gap-3 mb-4">
            <Avatar
              icon={<Bot className="w-5 h-5" />}
              classNames={{
                base: 'bg-primary/10 shrink-0',
                icon: 'text-primary',
              }}
              size="sm"
            />
            <Card classNames={{ base: 'bg-surface-elevated' }}>
              <CardBody className="p-3">
                <div className="flex items-center gap-2">
                  <Spinner size="sm" color="primary" />
                  <span className="text-sm text-foreground/70">Thinking...</span>
                </div>
              </CardBody>
            </Card>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-border bg-surface px-4 py-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about changes or request improvements..."
            disabled={isDisabled}
            className="flex-1 min-h-[40px] max-h-[120px] px-3 py-2 text-sm rounded-lg border border-border bg-surface-elevated text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none disabled:opacity-50 disabled:cursor-not-allowed"
            rows={1}
          />
          <Button
            isIconOnly
            color="primary"
            size="sm"
            isDisabled={!canSend}
            onPress={handleSubmit}
            aria-label="Send message"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-muted mt-1.5">
          Press Enter or <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">⌘</kbd>
          +Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
